/* eslint-disable @typescript-eslint/naming-convention */
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { faList } from '@fortawesome/free-solid-svg-icons/faList';
import { BehaviorSubject, Observable, of, Subscription, zip } from 'rxjs';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { catchError, filter, map, mergeMap, skip, take } from 'rxjs/operators';
import { HelperService } from '../../../services/helper/helper.service';
import { RoleInterface } from '../../../models/interface/role.interface';
import { StudyInterface } from '../../../models/interface/study/study.interface';
import { HelperDialogService } from '../../../services/helper/helper-dialog/helper-dialog.service';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { StudyActionTypes } from '../../../store/study/study.action';
import { StudyStore } from '../../../store/study/component-store/study.store';
import { OrganisationInterface } from '../../../models/interface/organisation/organisation.interface';
import { OrganisationActionTypes } from '../../../store/organisation/organisation.action';
import { PaginationData, PaginationListComponent } from '../../utils/pagination-list/pagination-list.component';

type Filter = {
  searchFilter: string;
  isCollab: boolean;
  private: 0 | 1 | 2;
};

/**
 * Component:
 * Group page displaying a list of groups - child component of GroupOverviewComponent;
 */
@Component({
  selector: 'app-group',
  templateUrl: './group.component.html',
  styleUrls: ['./group.component.scss'],
  providers: [StudyStore],
})
export class GroupComponent implements OnInit, OnDestroy {
  @ViewChild('paginatorStudy1') paginatorStudy1: PaginationListComponent;
  @ViewChild('paginatorStudy2') paginatorStudy2: PaginationListComponent;

  // Icons
  faList = faList;

  public isLoadingSubject = new BehaviorSubject<boolean>(true);
  public isLoading$ = this.isLoadingSubject.asObservable();

  public filter: Filter = {
    searchFilter: '',
    isCollab: true,
    private: 2,
  };

  public profileRoles$: Observable<RoleInterface[]>;
  public isCollapsed$: Observable<boolean>;
  public groupsData$: Observable<PaginationData<StudyInterface>>;
  public groups$: Observable<StudyInterface[]>;
  public pagedGroups$: Observable<StudyInterface[]>;

  public reloadEventSubject = new BehaviorSubject<string>('');

  public organisations$: Observable<OrganisationInterface[]>;
  public organisations: OrganisationInterface[] = [];

  public isCollapsedSubject = new BehaviorSubject<boolean>(true);

  public isAdmin$: Observable<boolean>;
  public isAdminSubject = new BehaviorSubject<boolean>(false);

  private searchTextSubject = new BehaviorSubject<string>('');

  private groupsDataSubject = new BehaviorSubject<PaginationData<StudyInterface>>({ items: [] });
  private groupsSubject = new BehaviorSubject<StudyInterface[]>([]);
  private pagedGroupsSubject = new BehaviorSubject<StudyInterface[]>([]);

  private allStudies$: Observable<StudyInterface[]>;

  private subscriptions: Subscription[] = [];

  constructor(
    private translateService: TranslateService,
    private helperService: HelperService,
    private helperDialogService: HelperDialogService,
    private studyStore: StudyStore,
    private actions$: Actions,
    private store: Store<{
      myRoles: RoleInterface[];
      organisations: OrganisationInterface[];
    }>,
  ) {
    this.profileRoles$ = store.select('myRoles');
    this.organisations$ = store.select('organisations');
    this.groupsData$ = this.groupsDataSubject.asObservable();
    this.groups$ = this.groupsSubject.asObservable();

    this.pagedGroups$ = this.pagedGroupsSubject.asObservable();
    this.isCollapsed$ = this.isCollapsedSubject.asObservable();
    this.isAdmin$ = this.isAdminSubject.asObservable();

    this.allStudies$ = this.studyStore.allStudies$;
  }

  public get helper() {
    return this.helperService;
  }

  public get helperDialog() {
    return this.helperDialogService;
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this.profileRoles$
        .pipe(
          filter((roles) => roles !== null),
          take(1),
        )
        .subscribe((roles: RoleInterface[]) => {
          if (this.helper.hasRoles(roles, /admin$/)) {
            this.isAdminSubject.next(true);
          }
          this.applyStudyFilter();
        }),
    );
    // Subscription to language change
    this.subscriptions.push(
      this.translateService.onLangChange.subscribe((_event: LangChangeEvent) => {
        this.applyStudyFilter();
      }),
    );
  }

  applyStudyFilter(setFirst?: boolean): void {
    this.isLoadingSubject.next(true);
    const organisations = this.reloadOrganisations();
    const studies = this.reloadStudies(setFirst);
    this.subscriptions.push(
      // Using zip, because forkJoin can be canceled by duplicate call of either request from other components
      zip(organisations, studies).subscribe(
        () => this.isLoadingSubject.next(false),
      ),
    );
  }

  public reloadOrganisations(): Observable<boolean> {
    const type = this.isAdminSubject.value
      ? OrganisationActionTypes.getOrganisationsAdminType
      : OrganisationActionTypes.getOrganisationsType;
    const payload = { include: 'owners,collaborators,roles' };
    this.store.dispatch({ type, payload });
    return this.organisations$.pipe(
      skip(1),
      take(1),
      mergeMap((result) => {
        this.organisations = result;
        return of(true);
      }),
      catchError((_error) => {
        this.organisations = [];
        return of(false);
      }),
    );
  }

  private getIsPrivate(isPrivate: Filter['private']) {
    return isPrivate === 2 ? undefined : isPrivate;
  }

  private reloadStudies(setFirst?: boolean) {
    const paginator = this.isAdminSubject.value ? this.paginatorStudy1 : this.paginatorStudy2;
    const pageIndex = paginator?.paginator ? paginator.paginator.pageIndex : 0;
    const pageSize = paginator?.paginator ? paginator.paginator.pageSize : 20;
    const paramPrivate = this.getIsPrivate(this.filter.private);
    this.store.dispatch({
      type: StudyActionTypes.getCollaboratingStudiesType,
      payload: {
        type: 'study,organisation',
        isPrivate: paramPrivate,
        include: 'owners,roles',
      },
    });
    return this.actions$.pipe(
      ofType(
        StudyActionTypes.getCollaboratingStudiesSuccessType,
        StudyActionTypes.getCollaboratingStudiesErrorType,
      ),
      take(1),
      map(
        (result: any) =>
          result.type === StudyActionTypes.getCollaboratingStudiesSuccessType
            ? result.response.body.data
            : [],
      ),
      mergeMap((studies: StudyInterface[]) => {
        return this.filter.isCollab
          ? this.setStudiesCollabOnly(studies, !!setFirst, pageIndex, pageSize)
          : this.setStudiesNonCollabOnly(studies, paramPrivate, !!setFirst, pageIndex, pageSize);
      }),
    );
  }

  private setStudiesCollabOnly(studies: StudyInterface[], setFirst: boolean, pageIndex: number, pageSize: number) {
    return this.performFilterAndPagination(setFirst, pageIndex, pageSize, studies);
  }

  private setStudiesNonCollabOnly(studies: StudyInterface[], paramPrivate: number, setFirst: boolean, pageIndex: number, pageSize: number) {
    this.studyStore.getAllStudies({ type: 'study', isPrivate: paramPrivate, include: 'owners' });
    return this.allStudies$.pipe(
      skip(1),
      take(1),
      mergeMap((result) => {
        const collaboratingStudies: StudyInterface[] = [];
        for (const study of result) {
          const studyId = study.id.toString();
          collaboratingStudies.push(
            studies.find((study) => study.id.toString() === studyId) || study,
          );
        }
        return this.performFilterAndPagination(setFirst, pageIndex, pageSize, collaboratingStudies);
      }),
    );
  }

  public updatePagedStudies(event: StudyInterface[]) {
    if (event) {
      this.pagedGroupsSubject.next(event);
    }
  }

  public reloadStudiesAfterCreation(event: string) {
    if (event) {
      this.applyStudyFilter();
      this.reloadEventSubject.next(event);
    }
  }

  public performFilterAndPagination(
    setFirst: boolean,
    pageIndex: number,
    pageSize: number,
    groups: StudyInterface[],
  ) {
    return this.helper
      .searchStudiesByInput(this.filter.searchFilter, this.searchTextSubject, groups, this.groupsSubject)
      .pipe(
        mergeMap(
          () => this.helper.setPagedContent(
            this.groupsSubject,
            this.pagedGroupsSubject,
            setFirst,
            pageIndex,
            pageSize,
            this.groupsDataSubject,
          ),
        ),
      );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }
}
